<script lang="ts">
  /**
   * A full-page screen laid over the app's column: Settings, a picker, a
   * table of scores, an account form, something to read. It is the frame for
   * anything that outgrew a bottom sheet, and it comes in three dresses:
   *
   * - `done`: on the sunken ground, the title at the left and the word
   *   **Done** at the right. For a screen of grouped rows, or a picker.
   * - `back`: on the ground, the word **Back** at the left and the title
   *   centred. For a form.
   * - `chevron`: on the ground, a chevron at the left and the title centred.
   *   For something to read.
   *
   * Built on the native `<dialog>` and `showModal()`, like `Sheet`: the
   * browser owns the focus trap, the inert page behind and the top layer, so
   * a sheet opened from here (a confirmation, an explanation) stacks on top
   * by itself. It keeps the contract every other overlay keeps:
   *
   * - **Back closes it.** Opening pushes one history entry through
   *   `historyStack.ts`; closing it any other way gives the same entry back.
   * - **Escape closes it, and only it**: the key's default is prevented and
   *   its propagation stopped, so a game listening underneath never sees it.
   * - **The parent owns `open`.** Every way of closing calls `onclose`.
   * - **Focus lands on `[data-autofocus]`** when the content marks one,
   *   otherwise on the page, and goes back where it was on close.
   *
   * The page (its title and its content) is rendered only while it is open,
   * so a closed page leaves no second copy of a form field or a test id in
   * the document, and anything it reads is read fresh each time.
   *
   * It is as wide as the app's column (`--ui-column`) and centred over it.
   * `wide` is for a game whose column widens from 1024 px (`--ui-column-wide`,
   * a sidebar beside the tab): the page then covers that whole column, so
   * nothing of the tab under it shows at the side, and its content grows to
   * 40rem, centred. Measured from TwinTen's
   * settings / picker (`done`), account (`back`) and help (`chevron`)
   * pages: the header's height, the title's step (body, 800) and each
   * dress's content inset are theirs.
   *
   * `dismissible={false}` (since 1.7.0) is for a question with one way on
   * and no way out (a first run's "Make it yours", `SettingsPage`'s
   * `prompt` frame), as a non-dismissible `Sheet` is: the header draws no
   * way out (whatever the dress), and Escape and Back do not close it; each
   * calls `onbackattempt` with which it was, and Back is armed again, so the
   * next Back lands here too rather than on the page underneath. `onclose`
   * is then never called. The content must hold the way on.
   * `describedby` (since 1.7.0) is the id of the sentence that says what
   * the page asks, for `aria-describedby`.
   *
   * `aside` is a word or two between the title and Done (whose scores these
   * are). Test ids: `testid` on the dialog; `testids.done` (default
   * `ui-page-done`) and `testids.back` (default `ui-page-back`) on the way out.
   */
  import type { Snippet } from 'svelte';

  import { pushOverlay } from '../historyStack.js';
  import { shellText } from '../i18n.svelte.js';
  import type { SheetBackAttempt } from '../types.js';
  import Icon from './Icon.svelte';
  import TextButton from './TextButton.svelte';

  interface Props {
    readonly open: boolean;
    /** The page's accessible name and its visible title. */
    readonly title: string;
    /** Called by Escape, Back and the header's own way out alike. */
    readonly onclose: () => void;
    readonly children: Snippet;
    readonly dress?: 'done' | 'back' | 'chevron';
    readonly wide?: boolean;
    /** A glyph before the title (a trophy on a scores page). */
    readonly lead?: Snippet | undefined;
    /** Between the title and Done: whose page this is ("Sam's scores"). */
    readonly aside?: Snippet | undefined;
    /** The name of the way out; the shell's own "Done" or "Back" by default. */
    readonly leaveLabel?: string | undefined;
    readonly testid?: string | undefined;
    readonly testids?: { readonly done?: string; readonly back?: string } | undefined;
    // ── 1.7.0 ──
    /** `false`: no way out drawn; Escape and Back call `onbackattempt` instead. Default `true`. */
    readonly dismissible?: boolean;
    /** A non-dismissible page's Escape or Back. */
    readonly onbackattempt?: ((reason: SheetBackAttempt) => void) | undefined;
    /** The id of the sentence that describes the page (`aria-describedby`). */
    readonly describedby?: string | undefined;
  }
  const {
    open,
    title,
    onclose,
    children,
    dress = 'done',
    wide = false,
    lead,
    aside,
    leaveLabel,
    testid,
    testids,
    dismissible = true,
    onbackattempt,
    describedby,
  }: Props = $props();

  const backTestid = $derived(testids?.back ?? 'ui-page-back');
  const doneTestid = $derived(testids?.done ?? 'ui-page-done');

  const titleId = $props.id();

  let dialog = $state<HTMLDialogElement | null>(null);

  function showNative(element: HTMLDialogElement): void {
    if (typeof element.showModal === 'function') {
      try {
        element.showModal();
        return;
      } catch {
        // Not connected yet, or already open non-modally: fall through.
      }
    }
    element.setAttribute('open', '');
  }

  // The native `close` event is queued, so it can arrive after this component
  // closed the element itself (or was unmounted while open). Each of our own
  // closes is counted and its event swallowed, so the parent is told once, by
  // whoever actually closed the page (the same bookkeeping `Sheet` keeps).
  let ownCloses = 0;
  let destroyed = false;

  $effect(() => () => {
    destroyed = true;
  });

  function hideNative(element: HTMLDialogElement): void {
    if (typeof element.close === 'function' && element.open) {
      ownCloses += 1;
      element.close();
    } else element.removeAttribute('open');
  }

  $effect(() => {
    const element = dialog;
    if (element === null || !open) return undefined;
    const previouslyFocused = document.activeElement;
    showNative(element);
    const target =
      element.querySelector<HTMLElement>('[data-autofocus]') ??
      element.querySelector<HTMLElement>('[data-page-panel]');
    target?.focus({ preventScroll: true });
    return () => {
      hideNative(element);
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  });

  /** One way out: close when dismissible, otherwise report the attempt (1.7.0). */
  function leave(reason: SheetBackAttempt): void {
    if (dismissible) onclose();
    else onbackattempt?.(reason);
  }

  // One history entry per open page. `onclose` is read when Back actually
  // arrives, never while this effect runs, so a parent passing a fresh arrow
  // function does not re-push anything.
  //
  // A non-dismissible page (1.7.0) answers Back as a non-dismissible `Sheet`
  // does: it arms a fresh entry straight away and reports the attempt, so
  // the next Back lands here again. `dismissible` is read when Back arrives.
  $effect(() => {
    if (!open) return undefined;
    let release: (() => void) | null = null;
    const arm = (): void => {
      release = pushOverlay(() => {
        release = null;
        if (dismissible) {
          onclose();
          return;
        }
        arm();
        onbackattempt?.('back');
      });
    };
    arm();
    return () => release?.();
  });

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    leave('escape');
  }

  /** The browser's own close request (Android may route Back here). */
  function onCancel(event: Event): void {
    event.preventDefault();
    leave('back');
  }

  function onNativeClose(): void {
    if (ownCloses > 0) {
      ownCloses -= 1;
      return;
    }
    if (destroyed || dialog === null || !dialog.isConnected) return;
    if (!open) return;
    if (dismissible) {
      onclose();
      return;
    }
    // A browser that closed it anyway: it is not the browser's to close.
    showNative(dialog);
    onbackattempt?.('back');
  }
</script>

<dialog
  bind:this={dialog}
  class="ui-page ui-page--{dress}"
  class:ui-page--wide={wide}
  aria-labelledby={open ? titleId : undefined}
  aria-describedby={open ? describedby : undefined}
  aria-modal="true"
  data-testid={testid}
  onkeydown={onKeydown}
  oncancel={onCancel}
  onclose={onNativeClose}
>
  {#if open}
    <div class="ui-page-panel" data-page-panel tabindex="-1">
      <div class="ui-page-header">
        {#if !dismissible}
          <!-- No way out (1.7.0): the title alone. -->
        {:else if dress === 'back'}
          <TextButton
            label={leaveLabel ?? shellText('back')}
            align="start"
            onclick={onclose}
            testid={backTestid}
          />
        {:else if dress === 'chevron'}
          <button
            type="button"
            class="ui-page-chevron"
            aria-label={leaveLabel ?? shellText('back')}
            data-testid={backTestid}
            onclick={onclose}
          >
            <Icon name="chevronLeft" size={22} />
          </button>
        {/if}
        <h2 id={titleId} class="ui-page-title">
          {#if lead !== undefined}<span class="ui-page-lead" aria-hidden="true"
              >{@render lead()}</span
            >{/if}{title}
        </h2>
        {#if aside !== undefined}
          <span class="ui-page-aside">{@render aside()}</span>
        {/if}
        {#if dress === 'done' && dismissible}
          <TextButton
            label={leaveLabel ?? shellText('done')}
            align="end"
            onclick={onclose}
            testid={doneTestid}
          />
        {:else}
          <span class="ui-page-spacer" aria-hidden="true"></span>
        {/if}
      </div>
      <div class="ui-page-body">{@render children()}</div>
    </div>
  {/if}
</dialog>

<style>
  .ui-page {
    box-sizing: border-box;
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    max-width: var(--ui-column);
    max-height: none;
    margin: 0 auto;
    padding: 0;
    border: 0;
    overflow-y: auto;
    background: var(--ui-ground);
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  /* No scrim: a full page covers the column, and what is beside it on a wide
     screen is the backdrop the column already sits on, with its own edge. */
  .ui-page::backdrop {
    background: transparent;
  }

  .ui-page--done {
    background: var(--ui-sunken);
  }

  @media (prefers-reduced-motion: no-preference) {
    .ui-page--done[open] {
      animation: ui-page-slide-in 0.2s ease-out;
    }
  }

  @keyframes ui-page-slide-in {
    from {
      transform: translateY(4%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (min-width: 1024px) {
    .ui-page--wide {
      max-width: var(--ui-column-wide);
    }

    .ui-page--wide .ui-page-panel {
      max-width: 40rem;
      margin: 0 auto;
    }
  }

  /* Beside the tablet rail (1.6.0): while an `AppFrame` draws its rail, the
     page covers what is right of it (the rail stays in view, inert behind
     the modal page) and its content grows to 40rem, centred, as `wide`'s
     does from 1024px. The rail is 5.5rem, after the frame's left safe area.
     The range is written out: no range syntax (Safari before 16.4). The
     selector matches while a frame with the rail is in the document,
     wherever this page sits; it assumes the frame starts at the window's
     left edge (AppFrame warns in development when it does not). */
  @media (min-width: 600px) and (max-width: 1023.98px) {
    :global(:root:has(.ui-frame--rail)) .ui-page {
      left: calc(5.5rem + env(safe-area-inset-left));
      width: auto;
      max-width: none;
      margin: 0;
    }

    :global(:root:has(.ui-frame--rail)) .ui-page-panel {
      max-width: 40rem;
      margin: 0 auto;
      padding-left: 0;
    }

    /* The same beside a frame's first frame (1.8.0, `firstFrame="auto"`),
       which shows the rail in this range by CSS alone. */
    :global(:root:has(.ui-frame--rail-auto)) .ui-page {
      left: calc(5.5rem + env(safe-area-inset-left));
      width: auto;
      max-width: none;
      margin: 0;
    }

    :global(:root:has(.ui-frame--rail-auto)) .ui-page-panel {
      max-width: 40rem;
      margin: 0 auto;
      padding-left: 0;
    }
  }

  /* Only the safe areas here: each dress insets its header and body itself. */
  .ui-page-panel {
    box-sizing: border-box;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    outline: none;
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
      env(safe-area-inset-left);
  }

  .ui-page-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 2.75rem;
  }

  /* done: the title 1.25rem in, Done's word as far in from the right. */
  .ui-page--done .ui-page-header {
    padding: 0.25rem 1.25rem 0;
  }

  .ui-page--back .ui-page-header {
    padding: 0 0.525rem;
  }

  .ui-page--chevron .ui-page-header {
    padding: 0.375rem 0.125rem;
  }

  .ui-page-title {
    flex: 0 1 auto;
    min-width: 0;
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font: var(--ui-text-body);
    font-weight: 800;
    color: var(--ui-ink);
  }

  .ui-page--back .ui-page-title,
  .ui-page--chevron .ui-page-title {
    flex: 1 1 auto;
    justify-content: center;
    text-align: center;
  }

  .ui-page-lead {
    display: inline-flex;
    color: var(--ui-gold);
  }

  .ui-page-aside {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 9rem;
    margin-left: auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: var(--ui-text-caption);
    font-weight: 700;
    color: var(--ui-ink);
  }

  .ui-page-chevron {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border: 0;
    border-radius: var(--ui-radius-pill);
    background: transparent;
    color: var(--ui-accent);
  }

  .ui-page-chevron:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 1px;
  }

  .ui-page-spacer {
    flex: 0 0 auto;
    width: 3rem;
  }

  .ui-page--chevron .ui-page-spacer {
    width: 2.75rem;
  }

  .ui-page-body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .ui-page--done .ui-page-body {
    padding: 0.25rem 1rem 1.5rem;
  }

  .ui-page--back .ui-page-body {
    padding: 0.5rem 1.1rem 1.5rem;
  }

  .ui-page--chevron .ui-page-body {
    padding: 0.5rem 1.5rem 2rem;
  }
</style>
